import { useMemo } from "react";
import { useLocation } from "react-router-dom";

interface WhatsAppWidgetProps {
  /** User's phone number to pre-fill (e.g., after form submission) */
  userPhone?: string;
  /** Force a specific message instead of auto-detecting from route */
  customMessage?: string;
  /** Force a specific insurance type label */
  insuranceType?: string;
  /** Variant: floating (small button) or inline (larger CTA) */
  variant?: "floating" | "inline" | "success";
  /** Additional CSS classes */
  className?: string;
  /** Children to render inside the button (for custom styling) */
  children?: React.ReactNode;
}

const PAGE_MESSAGE_MAP: Record<string, string> = {
  "/health-insurance": "I am Looking for Health Insurance. Please share the best plans with cashless hospital coverage.",
  "/term-insurance": "I am Looking for Term Insurance. Please suggest the best plans with high claim settlement ratio.",
  "/life-insurance": "I am Looking for Life Insurance. I need endowment or ULIP plans with guaranteed returns.",
  "/car-insurance": "I am Looking for Car Insurance. Please share comprehensive plans with zero depreciation cover.",
  "/travel-insurance": "I am Looking for Travel Insurance. I need coverage for medical emergencies and trip cancellation.",
  "/marine-insurance": "I am Looking for Marine Insurance. Please suggest cargo and transit coverage options.",
  "/corporate-insurance": "I am Looking for Corporate Insurance. Please share business liability and property coverage plans.",
  "/group-health-insurance": "I am Looking for Group Health Insurance for my employees. Please share corporate plans.",
  "/mutual-funds": "I am Looking for Mutual Fund Investments. Please suggest portfolios based on my risk profile.",
  "/sip-investments": "I am Looking for SIP Investments. I want to start systematic investment planning.",
  "/retirement-planning": "I am Looking for Retirement Planning. Please help me build a retirement corpus.",
  "/financial-planning": "I am Looking for Financial Planning. I need holistic goal-based planning and tax optimization.",
  "/claims-assistance": "I need Claims Assistance. Please help me with my insurance claim process.",
  "/compare-plans": "I am Looking to Compare Insurance Plans. Please help me find the best coverage.",
  "/calculators": "I am Looking for Insurance Calculators. Please guide me on premium estimation.",
  "/book-appointment": "I would like to Book a Free Consultation with an insurance advisor.",
  "/contact": "I am Looking for Insurance Advice. Please contact me with the best recommendations.",
  "/about": "I want to know more about ExpertAdvise.in and your insurance advisory services.",
  "/blog": "I am reading your blog. I have questions about insurance and financial planning.",
};

const DEFAULT_MESSAGE = "Hi ExpertAdvise, I need insurance advice. Please help me find the best plan.";

function getPageMessage(pathname: string): string {
  // Exact match first
  if (PAGE_MESSAGE_MAP[pathname]) {
    return PAGE_MESSAGE_MAP[pathname];
  }
  // Check if it's a blog detail page
  if (pathname.startsWith("/blog/")) {
    return "I was reading your article. I have questions about insurance and would like expert guidance.";
  }
  // Check if it's a calculator page
  if (pathname.startsWith("/calculator/")) {
    return "I used your insurance calculator. I would like expert help choosing the right plan.";
  }
  // Check if it's a city page
  if (pathname.startsWith("/cities/")) {
    const city = pathname.replace("/cities/", "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return `I am Looking for Insurance in ${city}. Please suggest the best plans available in my city.`;
  }
  // Check if it's a service page (fallback for any /service/* pattern not in the map)
  for (const [route, msg] of Object.entries(PAGE_MESSAGE_MAP)) {
    if (pathname.includes(route.replace("/", ""))) {
      return msg;
    }
  }
  return DEFAULT_MESSAGE;
}

export function getWhatsAppUrl(
  userPhone?: string,
  customMessage?: string,
  insuranceType?: string,
  pathname?: string
): string {
  const baseUrl = "https://wa.me/919999365655";

  let message: string;
  if (customMessage) {
    message = customMessage;
  } else if (insuranceType) {
    message = `I am Looking for ${insuranceType}. Please share the best plans and recommendations.`;
  } else if (pathname) {
    message = getPageMessage(pathname);
  } else {
    message = DEFAULT_MESSAGE;
  }

  // Append phone number context if provided
  if (userPhone) {
    message += ` My contact number is ${userPhone}.`;
  }

  const encodedMessage = encodeURIComponent(message);
  return `${baseUrl}?text=${encodedMessage}`;
}

export default function WhatsAppWidget({
  userPhone,
  customMessage,
  insuranceType,
  variant = "floating",
  className = "",
  children,
}: WhatsAppWidgetProps) {
  const location = useLocation();

  const whatsappUrl = useMemo(
    () => getWhatsAppUrl(userPhone, customMessage, insuranceType, location.pathname),
    [userPhone, customMessage, insuranceType, location.pathname]
  );

  const isFloating = variant === "floating";
  const isSuccess = variant === "success";

  if (isSuccess) {
    return (
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 rounded-full transition-colors shadow-lg hover:shadow-xl whitespace-nowrap ${className}`}
      >
        <i className="ri-whatsapp-line text-lg"></i>
        {children || "Continue on WhatsApp"}
      </a>
    );
  }

  if (isFloating) {
    return (
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center gap-2 bg-white text-slate-800 px-4 py-2.5 rounded-full shadow-lg border border-slate-100 hover:shadow-xl transition-all hover:scale-105 group ${className}`}
        title="WhatsApp Expert"
      >
        <span className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center shrink-0">
          <i className="ri-whatsapp-line text-white text-base"></i>
        </span>
        <span className="text-sm font-semibold hidden sm:block group-hover:text-green-600 transition-colors whitespace-nowrap">
          WhatsApp Expert
        </span>
      </a>
    );
  }

  // inline variant
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors shadow-md hover:shadow-lg whitespace-nowrap ${className}`}
    >
      <i className="ri-whatsapp-line text-lg"></i>
      {children || "Chat on WhatsApp"}
    </a>
  );
}

/**
 * Hook to get the current page's WhatsApp message and URL
 */
export function useWhatsAppContext() {
  const location = useLocation();

  const message = useMemo(
    () => getPageMessage(location.pathname),
    [location.pathname]
  );

  const url = useMemo(
    () => getWhatsAppUrl(undefined, undefined, undefined, location.pathname),
    [location.pathname]
  );

  return { message, url, pathname: location.pathname };
}